import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import SearchAndFilter from "./SearchAndFilter";
import { useRestaurantData } from "./hooks/useRestaurantData";
import { useOrderManagement } from "./hooks/useOrderManagement";
import { useCustomerManagement } from "./hooks/useCustomerManagement";
import OrderSection from "./order-section/OrderSection";
import { usePaymentTheme } from "../respayment/hooks/usePaymentTheme";
import { PageTitle } from "src/shared/PageTitle";
import { useEditOrderHandler } from "./hooks/useEditOrderHandler";
import { useOrderModificationTracker } from "./hooks/useOrderModificationTracker";
import { useWindowManagement } from "./hooks/useWindowManagement";
import { useOrderOperations } from "src/business-types/restaurant/modules/home/Reshome/hooks/useOrderOperations";
import CategoryCarousel from "src/business-types/restaurant/modules/home/Reshome/components/CategoryCarousel";
import ProductDisplaySection from "src/business-types/restaurant/modules/home/Reshome/components/ProductDisplaySection";
import LoadingScreen from "src/shared/components/LoadingScreen";
import { ToastContainer } from "react-toastify";
import { getOrder } from "src/shared/services/servicesv2";
import { useUser } from "src/shared/context/UserContext";
import OpenCloseDayPopup from "src/shared/modules/admin/shift/OpenCloseDayPopup";
import { useBusinessOperations } from "src/shared/modules/admin/shift/useBusinessOperations";
import api from "src/features/auth/axiosConfig";
import SubscriptionDialog from "src/shared/modules/admin/auth/components/SubscriptionDialog";
import CustomerDialogWrapper from "./components/CustomerDialogWrapper";

const Reshome = () => {
  const headerRef = useRef<{ refreshOrders: () => void }>(null);
  const { orderid } = useParams<{ orderid: any }>();
  const { categories, products, loading } = useRestaurantData();
  const [selectedCategoryradio, setSelectedCategoryradio] = useState<string>("none");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [tableNumber, setTableNumber] = useState<string>("");
  const [guess, setGuess] = useState<string>("");
  const [newopen, setNewOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [isDelivery, setIsDelivery] = useState(false);
  const [deliveryFee, setDeliveryFee] = useState<number>(0);
  const { userConfigration, subscriptionInfo } = useUser();
  const { refreshStatus } = useBusinessOperations();
  const [showDialog, setShowDialog] = useState(false);
  const [message, setMessage] = useState<React.ReactNode>("");
  // const [orderType, setOrderType] = useState<string>(isDelivery ? "Delivery" : "Table");

  const toBoolean = (value: any) => value === true || value === "true";
  const deliveryFeeEnabled = toBoolean(userConfigration?.deliveryFeeEnabled);
  const [showOpenDayPopup, setShowOpenDayPopup] = useState(false);
  const [openableShifts, setOpenableShifts] = useState<string[]>([]);
  const [businessId, setBusinessId] = useState<string | null>(null);

  // ===================== OPEN DAY LOGIC =========================

  useEffect(() => {
    if (toBoolean(userConfigration.businessOperationEnable) === true) {
      api.get(`/app/pos/v2/business_operations?status=OPEN`).then((res) => {
        const BusinessDay = res.data.result[0];
        if (BusinessDay?.status === "OPEN") {
          setShowOpenDayPopup(false);
        } else {
          setShowOpenDayPopup(true);
        }
      });
    } else {
      setShowOpenDayPopup(false);
    }
  }, [userConfigration.businessOperationEnable]);

  const { fontClass } = usePaymentTheme();
  useWindowManagement();

  const { selectedProducts, setSelectedProducts, discount, setDiscount, isEditing, setIsEditing, addProduct, ProductDecrement, calculateSubtotal, calculateTotal, incrementCart, decrementCart, deleteProduct, handleNoteChange, editingNote, setEditingNote, parcelFee, setParcelFee, } = useOrderManagement();
  const { customers, filteredCustomers, setFilteredCustomers, pagenumber, setPagenumber, total, firstName, setFirstName, middleName, setMiddleName, lastName, setLastName, phoneNumber, setPhoneNumber, customerAdd, setCustomerAdd, setSearchTerm, searchTerm, fetchCustomerDetails, } = useCustomerManagement();
  const { oldProducts, setOldProducts } = useEditOrderHandler({ products, setSelectedProducts, setDiscount, setIsEditing, setFirstName, setMiddleName, setLastName, setPhoneNumber, setTableNumber, setGuess, setOrderType: (type: string) => setIsDelivery(type === "Delivery"), setDeliveryFee, setParcelFee });
  const { isOrderModified } = useOrderModificationTracker({ selectedProducts, isEditing, oldProducts, discount, tableNumber, guess, orderType: isDelivery ? "Delivery" : "Table", deliveryFee });

  const { open, setOpen, handleCancelOrder, handleUpdateOrder, handleSubmit, resetFormFields } = useOrderOperations({
    selectedProducts, setSelectedProducts, firstName, setFirstName, middleName, setMiddleName, lastName, setLastName, phoneNumber, setPhoneNumber, tableNumber, setTableNumber, guess, setGuess,
    discount, setDiscount, oldProducts, setOldProducts, isEditing, setIsEditing, calculateSubtotal, calculateTotal, isDelivery, headerRef, setCustomerAdd, setSelectedCategory, setSelectedCategoryradio, setSearchQuery, setCurrentPage, fetchCustomerDetails, deliveryFee, setDeliveryFee, parcelFee, setParcelFee
  });

  // useEffect(() => {
  //   setIsDelivery(orderType === "Delivery");
  // }, [orderType]);

  useEffect(() => {
    if (!subscriptionInfo) return;

    const alreadyShown = sessionStorage.getItem("subscription_dialog_shown");
    if (alreadyShown === "true") return;

    // GRACE PERIOD CASE
    if (subscriptionInfo.status === "grace_period") {
      const days = subscriptionInfo.daysRemaining;

      const formattedMessage = (
        <>
          This is a reminder that your current subscription will expire in <strong>{days} day(s)</strong>. Please renew now to avoid service disruption.
        </>
      );

      setMessage(formattedMessage);
      setShowDialog(true);
      sessionStorage.setItem("subscription_dialog_shown", "true");
      return;
    }

    // WARNING CASE
    if (subscriptionInfo.warning) {
      setMessage(subscriptionInfo.warning);
      setShowDialog(true);
      sessionStorage.setItem("subscription_dialog_shown", "true");
      return;
    }
  }, [subscriptionInfo]);

  // Keep your existing useEffect for orderid
  useEffect(() => {
    const fetchOrder = async (id: string) => {
      try {
        await getOrder(id);
        //  api.get(`/app/pos/v2/order${id}`);
      } catch (err) {
        console.error(err);
      }
    };
    if (orderid) {
      fetchOrder(orderid);
    }
  }, [orderid]);

  // Reset to first page when categories change
  useEffect(() => {
    // document.title = pageTitle("Home");
    setCurrentPage(0);
  }, [categories]);

  const filteredProducts = useMemo(() => {
    if (!products?.length) return [];

    const search = searchQuery?.trim().toLowerCase();
    const type = selectedCategoryradio?.toLowerCase();

    return products.filter(({ type: itemtype, productName, categoryId }) => {
      if (type && type !== "none" && itemtype?.toLowerCase() !== type) return false;
      if (search && !productName?.toLowerCase().includes(search)) return false;
      if (selectedCategory && categoryId !== selectedCategory) return false;
      return true;
    });
  }, [products, selectedCategory, selectedCategoryradio, searchQuery]);

  const handleOpenDialog = () => {
    setNewOpen(true);
  };

  const handleCloseDialog = () => {
    setNewOpen(false);
  };

  const handleReload = () => {
    resetFormFields();
    handleCloseDialog();
  };

  const handleNoteClick = (productId: string) => {
    setEditingNote(productId);
  };

  const handleNoteBlur = (orderId: string) => {
    setEditingNote(null);
  };

  // Handle successful open day - with debounce to prevent multiple calls
  const handleOpenDaySuccess = useCallback(
    (newShiftId?: string) => {
      if (newShiftId) {
        localStorage.setItem("activeShiftId", newShiftId);
        setBusinessId(newShiftId);

        // Dispatch custom event to notify ProfileSection
        window.dispatchEvent(
          new CustomEvent("shiftStatusChanged", {
            detail: { status: "OPEN", shiftId: newShiftId },
          }),
        );

        // Also directly refresh status
        setTimeout(() => {
          refreshStatus();
        }, 100);
      }
      setShowOpenDayPopup(false);
    },
    [refreshStatus],
  );

  return (
    <div className={`flex flex-col w-full h-full overflow-hidden bg-[#F9F8FA] ${fontClass}`}>
      <PageTitle title="- Point of Sale › Home" />
      <div className="thin-scrollbar w-full flex-1 flex flex-col lg:flex-row gap-1 p-1 h-full overflow-auto">
        <div className="flex flex-col lg:flex-row w-full h-full gap-2">
          {/* Left Panel */}
          <div className="w-full lg:w-[60%] h-full flex flex-col bg-[#F9F8FA] rounded-lg shadow-md">
            <SearchAndFilter
              selectedCategoryradio={selectedCategoryradio}
              setSelectedCategoryradio={setSelectedCategoryradio}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              handleOpenDialog={handleOpenDialog}
              newopen={newopen}
              handleCloseDialog={handleCloseDialog}
              handleReload={handleReload}
            />

            <CategoryCarousel categories={categories} selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory} currentPage={currentPage} setCurrentPage={setCurrentPage} />

            <ProductDisplaySection
              key={`${selectedCategory}-${selectedCategoryradio}-${searchQuery}`}
              filteredProducts={filteredProducts}
              selectedProducts={selectedProducts}
              handleIncrement={addProduct}
              // handleIncrement={handleIncrement}
              // handleDecrement={() => {}}
              handleDecrement={ProductDecrement}
            />
          </div>

          <OrderSection
            isOrderModified={isOrderModified}
            isDelivery={isDelivery}
            setIsDelivery={setIsDelivery}
            tableNumber={tableNumber}
            guess={guess}
            setGuess={setGuess}
            setTableNumber={setTableNumber}
            oldProducts={oldProducts}
            selectedProducts={selectedProducts}
            editingNote={editingNote}
            handleNoteChange={handleNoteChange}
            handleNoteClick={handleNoteClick}
            handleNoteBlur={handleNoteBlur}
            deleteProduct={deleteProduct}
            handleIncrement={incrementCart} // ✅ OrderedProduct
            handleDecrement={decrementCart}
            calculateSubtotal={calculateSubtotal}
            discount={discount}
            setDiscount={setDiscount}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            calculateTotal={calculateTotal}
            handleCancelOrder={handleCancelOrder}
            handleUpdatOrder={() => handleUpdateOrder(isOrderModified)}
            setOpen={setOpen}
            open={open}
            customers={customers}
            filteredCustomers={filteredCustomers}
            setFilteredCustomers={setFilteredCustomers}
            pagenumber={pagenumber}
            setPagenumber={setPagenumber}
            total={total}
            sendorder={handleSubmit}
            setCustomerAdd={setCustomerAdd}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            resetFormFields={resetFormFields}
            deliveryFee={deliveryFee}
            setDeliveryFee={setDeliveryFee}
            deliveryFeeEnabled={deliveryFeeEnabled}
            parcelFee={parcelFee}
            setParcelFee={setParcelFee}
          />
        </div>
        {loading && <LoadingScreen />}
      </div>

      <CustomerDialogWrapper
        customerAdd={customerAdd}
        setCustomerAdd={setCustomerAdd}
        firstName={firstName}
        setFirstName={setFirstName}
        middleName={middleName}
        setMiddleName={setMiddleName}
        lastName={lastName}
        setLastName={setLastName}
        phoneNumber={phoneNumber}
        setPhoneNumber={setPhoneNumber}
        tableNumber={tableNumber}
        setTableNumber={setTableNumber}
        handleSubmit={handleSubmit}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
      />
      <SubscriptionDialog
        open={showDialog}
        message={message}
        onClose={() => setShowDialog(false)}
        onRenew={() => {
          setShowDialog(false);
          // navigate to subscription page
        }}
      />
      <ToastContainer />
      {showOpenDayPopup && <OpenCloseDayPopup isOpen={showOpenDayPopup} mode="open" shiftTypes={openableShifts} onClose={() => setShowOpenDayPopup(false)} onSuccess={handleOpenDaySuccess} />}
    </div>
  );
};

export default Reshome;
